import React, { useState } from "react"
import AddToCollection from "@love-irish/resource-add-to-collection"
import ResourceMenu from "@love-irish/resource-menu"
import ShareModal from "@love-irish/resource-share-modal"
import { UpsellModal } from "@love-irish/upsell-modal"
import { useSaveToggle } from "@love-irish/resource-use-save-toggle"
import {
  IconShare,
  IconLink,
  IconStar,
  IconStarFilled,
  IconUserPlus,
  IconMarkdown,
} from "@tabler/icons-react"

export interface ResourceActionsProps {
  item: {
    gid?: string
    id?: string | number
    type?: string
    saved?: boolean
    path?: string | null
    markdownPath?: string | null
    name?: string | null
    [key: string]: unknown
  }
  currentUser?: unknown
}

/**
 * The action menu for a resource list item: save (star), add-to-collection, share (modal), and a
 * copy-link overflow entry. Wraps the shared ResourceMenu so every resource row exposes the same
 * actions from one place.
 */
export const ResourceActions: React.FC<ResourceActionsProps> = ({ item }) => {
  const [isShareOpen, setIsShareOpen] = useState(false)
  const [isSaveUpsellOpen, setIsSaveUpsellOpen] = useState(false)

  // Clean plain-text Markdown alternate (for AI/LLMs), served by MarkdownRenderable at `${path}.md`.
  // encodeURI so accented fada slugs (e.g. /codex/béarla) don't break the link.
  const markdownUrl = item.path ? encodeURI(`${item.path}.md`) : undefined

  const {
    saved,
    loading: saveLoading,
    toggle: toggleSave,
  } = useSaveToggle({
    resourceType: item.type || "",
    resourceId: item.gid || String(item.id ?? ""),
    name: item.name || undefined,
    sanitizedName: item.sanitizedName || undefined,
    initialSaved: !!item.saved,
    onRequireAuth: () => setIsSaveUpsellOpen(true),
  })

  const shareUrl =
    typeof window !== "undefined" && item.path
      ? `${window.location.origin}${item.path}`
      : item.path || ""

  const copyLink = () => {
    if (shareUrl) navigator.clipboard?.writeText(shareUrl)
  }

  return (
    <>
      <ResourceMenu>
        <ResourceMenu.Item
          icon={IconStar}
          activeIcon={IconStarFilled}
          active={saved}
          label={saved ? "Saved" : "Save"}
          onClick={toggleSave}
          disabled={saveLoading}
        />
        <ResourceMenu.Item label="Add to collection">
          <AddToCollection itemId={item.gid} align="end" />
        </ResourceMenu.Item>
        <ResourceMenu.Item
          icon={IconShare}
          label="Share"
          onClick={() => setIsShareOpen(true)}
        />
        <ResourceMenu.MenuItem
          icon={IconLink}
          label="Copy link"
          onClick={copyLink}
        />
        {markdownUrl && (
          <ResourceMenu.MenuItem
            icon={IconMarkdown}
            label="View as Markdown [.md]"
            href={markdownUrl}
            newTab
          />
        )}
      </ResourceMenu>

      <ShareModal
        open={isShareOpen}
        onOpenChange={setIsShareOpen}
        url={shareUrl}
        title={item.name || undefined}
      />

      <UpsellModal
        isOpen={isSaveUpsellOpen}
        onClose={() => setIsSaveUpsellOpen(false)}
        featureName="saved_resources"
        analyticsProperties={{ resource_type: item.type }}
        headerGradient="bg-linear-to-br from-warning-400 to-warning-600"
        icon={<IconStarFilled size={32} />}
        title="Save this for later"
        description="Create a free account to save records to your library and build your own collections."
        primaryCta={{
          label: "Create an account",
          href: "/signup",
          icon: IconUserPlus,
        }}
        secondaryCta={{ label: "Login", href: "/login" }}
      />
    </>
  )
}

export default ResourceActions
